import { clone, debounce, identity } from 'lodash';
import { useCallback, useRef, useState } from 'react';

export default function useLoadedData({
  loadFn,
  getDataFromResponseFn = identity(response => response),
  initialLoading = true,
  initialData = null,
  isCacheEnabled = false,
}) {
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [data, setData] = useState(initialData);
  const [error, setError] = useState(null);
  const cacheMap = useRef({});

  const loadData = useCallback(
    debounce(async (...params) => {
      setIsLoading(true);
      try {
        const cacheKey = JSON.stringify(params);
        if (cacheMap.current[cacheKey] && isCacheEnabled) {
          setData(clone(cacheMap.current[cacheKey]));
          return;
        }

        const response = await loadFn(...params);
        const result = getDataFromResponseFn(response);
        setData(result);
        if (isCacheEnabled) cacheMap.current[cacheKey] = result;
      } catch (loadError) {
        setError(loadError);
        console.error(loadError);
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [getDataFromResponseFn, loadFn, isCacheEnabled]
  );

  return {
    isLoading,
    data,
    error,
    loadData,
    setData,
    setIsLoading,
  };
}
